import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Terminal as TerminalIcon, Power, Trash2, RefreshCw, AlertCircle, Wifi, WifiOff, History } from 'lucide-react';
import { toast } from 'sonner';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from 'xterm-addon-web-links';
import 'xterm/css/xterm.css';

const RemoteTerminalTab = ({ agentId }) => {
  const [terminalType, setTerminalType] = useState('powershell');
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  
  const terminalRef = useRef(null);
  const xtermRef = useRef(null);
  const wsRef = useRef(null);
  const fitAddonRef = useRef(null);
  const inputBufferRef = useRef('');
  const connectedRef = useRef(false);
  const reconnectTimeoutRef = useRef(null);
  const keepaliveIntervalRef = useRef(null);
  
  // Command history
  const commandHistoryRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const savedInputRef = useRef(''); // Save current input when navigating history
  
  // Tab completion state
  const tabCompletionsRef = useRef([]);
  const tabIndexRef = useRef(0);
  const lastTabInputRef = useRef('');
  const isWaitingForCompletionRef = useRef(false);

  // Maximum reconnection attempts
  const MAX_RECONNECT_ATTEMPTS = 3;
  const KEEPALIVE_INTERVAL = 25000; // 25 seconds
  const MAX_HISTORY = 100;

  // Initialize terminal
  useEffect(() => {
    const initTerminal = () => {
      if (!terminalRef.current || xtermRef.current) return;

      try {
        const term = new Terminal({
          cursorBlink: true,
          fontSize: 14,
          fontFamily: 'Consolas, "Courier New", monospace',
          theme: {
            background: '#1e1e1e',
            foreground: '#d4d4d4',
            cursor: '#ffffff',
            cursorAccent: '#1e1e1e',
            selection: 'rgba(38, 79, 120, 0.5)',
            black: '#000000',
            red: '#cd3131',
            green: '#0dbc79',
            yellow: '#e5e510',
            blue: '#2472c8',
            magenta: '#bc3fbc',
            cyan: '#11a8cd',
            white: '#e5e5e5',
            brightBlack: '#666666',
            brightRed: '#f14c4c',
            brightGreen: '#23d18b',
            brightYellow: '#f5f543',
            brightBlue: '#3b8eea',
            brightMagenta: '#d670d6',
            brightCyan: '#29b8db',
            brightWhite: '#ffffff'
          },
          rows: 24,
          cols: 100,
          scrollback: 5000,
          convertEol: true,
          allowProposedApi: true
        });

        const fitAddon = new FitAddon();
        const webLinksAddon = new WebLinksAddon();
        
        term.loadAddon(fitAddon);
        term.loadAddon(webLinksAddon);
        term.open(terminalRef.current);
        
        // Fit after a small delay to ensure container has dimensions
        setTimeout(() => {
          try {
            fitAddon.fit();
          } catch (e) {
            console.error('Error fitting terminal:', e);
          }
        }, 100);
        
        fitAddonRef.current = fitAddon;
        xtermRef.current = term;

        term.writeln('\x1b[1;34m╔════════════════════════════════════════╗\x1b[0m');
        term.writeln('\x1b[1;34m║       Remote Terminal Console          ║\x1b[0m');
        term.writeln('\x1b[1;34m╚════════════════════════════════════════╝\x1b[0m');
        term.writeln('');
        term.writeln('Select terminal type and click \x1b[1;32mConnect\x1b[0m to start.');
        term.writeln('');

        // Handle terminal input
        term.onData((data) => {
          if (!connectedRef.current) return;
          
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            handleTerminalInput(term, data);
          }
        });

        // Handle window resize
        const handleResize = () => {
          if (fitAddonRef.current && xtermRef.current) {
            try {
              fitAddonRef.current.fit();
            } catch (e) {
              console.error('Error resizing terminal:', e);
            }
          }
        };
        window.addEventListener('resize', handleResize);

        return () => {
          window.removeEventListener('resize', handleResize);
        };
      } catch (error) {
        console.error('Error initializing terminal:', error);
      }
    };

    // Delay initialization to ensure DOM is ready
    const timer = setTimeout(initTerminal, 100);
    
    return () => {
      clearTimeout(timer);
      cleanup();
    };
  }, []);

  // Handle terminal input with Tab completion and history
  const handleTerminalInput = useCallback((term, data) => {
    // Tab key
    if (data === '\t') {
      handleTabCompletion(term);
      return;
    }
    
    // Reset tab completion state on any other key
    if (data !== '\t') {
      tabCompletionsRef.current = [];
      tabIndexRef.current = 0;
    }
    
    // Enter key
    if (data === '\r') {
      term.write('\r\n');
      
      const command = inputBufferRef.current;
      inputBufferRef.current = '';
      historyIndexRef.current = -1;
      savedInputRef.current = '';
      
      if (command.trim()) {
        // Add to history (avoid duplicates of last command)
        if (commandHistoryRef.current[0] !== command) {
          commandHistoryRef.current.unshift(command);
          if (commandHistoryRef.current.length > MAX_HISTORY) {
            commandHistoryRef.current.pop();
          }
        }
        
        wsRef.current.send(JSON.stringify({
          type: 'input',
          data: command
        }));
      } else {
        // Empty command, request new prompt
        wsRef.current.send(JSON.stringify({
          type: 'input',
          data: ''
        }));
      }
      return;
    }
    
    // Backspace
    if (data === '\u007F' || data === '\b') {
      if (inputBufferRef.current.length > 0) {
        inputBufferRef.current = inputBufferRef.current.slice(0, -1);
        term.write('\b \b');
      }
      return;
    }
    
    // Ctrl+C
    if (data === '\u0003') {
      inputBufferRef.current = '';
      historyIndexRef.current = -1;
      term.write('^C\r\n');
      wsRef.current.send(JSON.stringify({
        type: 'input',
        data: '\u0003'
      }));
      return;
    }
    
    // Ctrl+L (clear screen)
    if (data === '\u000c') {
      term.clear();
      wsRef.current.send(JSON.stringify({
        type: 'input',
        data: ''
      }));
      return;
    }
    
    // Arrow Up - history previous
    if (data === '\u001b[A') {
      navigateHistory(term, 'up');
      return;
    }
    
    // Arrow Down - history next
    if (data === '\u001b[B') {
      navigateHistory(term, 'down');
      return;
    }
    
    // Arrow Left
    if (data === '\u001b[D') {
      // TODO: implement cursor movement
      return;
    }
    
    // Arrow Right
    if (data === '\u001b[C') {
      // TODO: implement cursor movement
      return;
    }
    
    // Home key
    if (data === '\u001b[H' || data === '\u0001') {
      return;
    }
    
    // End key
    if (data === '\u001b[F' || data === '\u0005') {
      return;
    }
    
    // Regular printable character
    if (data.length === 1 && data.charCodeAt(0) >= 32) {
      inputBufferRef.current += data;
      term.write(data);
      return;
    }
    
    // Pasted text
    if (data.length > 1 && !data.startsWith('\u001b')) {
      inputBufferRef.current += data;
      term.write(data);
      return;
    }
  }, []);

  // Navigate command history
  const navigateHistory = useCallback((term, direction) => {
    const history = commandHistoryRef.current;
    if (history.length === 0) return;
    
    // Save current input when starting to navigate
    if (historyIndexRef.current === -1 && direction === 'up') {
      savedInputRef.current = inputBufferRef.current;
    }
    
    if (direction === 'up') {
      if (historyIndexRef.current < history.length - 1) {
        historyIndexRef.current++;
      }
    } else {
      if (historyIndexRef.current > -1) {
        historyIndexRef.current--;
      }
    }
    
    // Clear current line
    const clearLength = inputBufferRef.current.length;
    term.write('\b \b'.repeat(clearLength));
    
    // Get new value
    let newValue;
    if (historyIndexRef.current === -1) {
      newValue = savedInputRef.current;
    } else {
      newValue = history[historyIndexRef.current] || '';
    }
    
    inputBufferRef.current = newValue;
    term.write(newValue);
  }, []);

  // Handle Tab completion
  const handleTabCompletion = useCallback((term) => {
    if (isWaitingForCompletionRef.current) return;
    
    const currentInput = inputBufferRef.current;
    
    // If we have cached completions and input hasn't changed, cycle through them
    if (tabCompletionsRef.current.length > 0 && lastTabInputRef.current === currentInput) {
      tabIndexRef.current = (tabIndexRef.current + 1) % tabCompletionsRef.current.length;
      applyCompletion(term, tabCompletionsRef.current[tabIndexRef.current]);
      return;
    }
    
    // Request new completions from server
    lastTabInputRef.current = currentInput;
    isWaitingForCompletionRef.current = true;
    
    wsRef.current.send(JSON.stringify({
      type: 'tab_complete',
      data: currentInput
    }));
    
    // Timeout for completion request
    setTimeout(() => {
      isWaitingForCompletionRef.current = false;
    }, 3000);
  }, []);

  // Apply a completion to the terminal
  const applyCompletion = useCallback((term, completion) => {
    if (!completion) return;
    
    // Clear current input
    const clearLength = inputBufferRef.current.length;
    term.write('\b \b'.repeat(clearLength));
    
    // Write completion
    inputBufferRef.current = completion;
    term.write(completion);
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (keepaliveIntervalRef.current) {
      clearInterval(keepaliveIntervalRef.current);
      keepaliveIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  // Start keepalive pings
  const startKeepalive = useCallback(() => {
    if (keepaliveIntervalRef.current) {
      clearInterval(keepaliveIntervalRef.current);
    }
    
    keepaliveIntervalRef.current = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, KEEPALIVE_INTERVAL);
  }, []);

  // Connect to terminal
  const connectTerminal = useCallback((isReconnect = false) => {
    if (!xtermRef.current) return;
    
    const term = xtermRef.current;
    
    if (!isReconnect) {
      term.clear();
      setReconnectAttempt(0);
    }
    
    setConnecting(true);
    setConnectionError(null);
    
    term.writeln(`\x1b[1;33m${isReconnect ? '↻ Reconnecting' : '⚡ Connecting'} to ${terminalType.toUpperCase()}...\x1b[0m`);

    // Get backend URL from environment
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsUrl = backendUrl.replace(/^https?:/, `${wsProtocol}:`);
    
    const ws = new WebSocket(`${wsUrl}/api/ws/terminal/${agentId}/${terminalType}`);
    
    ws.onopen = () => {
      setConnected(true);
      setConnecting(false);
      connectedRef.current = true;
      setReconnectAttempt(0);
      term.writeln('\x1b[1;32m✓ Connected successfully!\x1b[0m\r\n');
      if (!isReconnect) {
        toast.success('Terminal connected');
      }
      term.focus();
      startKeepalive();
    };

    // Helper function to strip CLIXML from output
    const stripClixml = (text) => {
      if (!text) return '';
      // Remove CLIXML header
      text = text.replace(/#<\s*CLIXML\s*/gi, '');
      // Remove Objs blocks (entire XML structure)
      text = text.replace(/<Objs[\s\S]*?<\/Objs>/g, '');
      // Remove individual XML elements
      text = text.replace(/<Obj\s+[\s\S]*?<\/Obj>/g, '');
      text = text.replace(/<TN[\s\S]*?<\/TN>/g, '');
      text = text.replace(/<TNRef[^>]*\/>/g, '');
      text = text.replace(/<MS>[\s\S]*?<\/MS>/g, '');
      text = text.replace(/<Props>[\s\S]*?<\/Props>/g, '');
      text = text.replace(/<S\s+[^>]*>[^<]*<\/S>/g, '');
      text = text.replace(/<I64[^>]*>[^<]*<\/I64>/g, '');
      text = text.replace(/<PR[^>]*>[\s\S]*?<\/PR>/g, '');
      text = text.replace(/<LST>[\s\S]*?<\/LST>/g, '');
      text = text.replace(/<ToString>[^<]*<\/ToString>/g, '');
      text = text.replace(/<DT[^>]*>[^<]*<\/DT>/g, '');
      text = text.replace(/<U32[^>]*>[^<]*<\/U32>/g, '');
      text = text.replace(/<B\s+[^>]*>[^<]*<\/B>/g, '');
      text = text.replace(/<Nil\s*\/>/g, '');
      // Remove any remaining XML tags with RefId
      text = text.replace(/<[^>]+RefId="[^"]*"[^>]*\/?>/g, '');
      // Remove empty/orphaned tags
      text = text.replace(/<\/?[A-Z][a-z]*[^>]*>/g, '');
      // Clean up excessive newlines
      text = text.replace(/\n{3,}/g, '\n\n');
      return text.trim();
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        if (message.type === 'output') {
          // Clean output - remove any remaining CLIXML artifacts
          let output = message.data || '';
          
          // Apply aggressive CLIXML stripping
          output = stripClixml(output);
          
          // Write cleaned output (only if there's something meaningful)
          if (output.trim() || output.includes('>')) {
            term.write(output);
          }
        } else if (message.type === 'tab_completions') {
          // Handle tab completion response
          isWaitingForCompletionRef.current = false;
          const completions = message.completions || [];
          
          // Filter out any completions that look like CLIXML garbage
          const cleanCompletions = completions.filter(c => 
            c && !c.includes('CLIXML') && !c.startsWith('<') && !c.includes('Objs')
          );
          
          if (cleanCompletions.length === 0) {
            // No completions - beep or do nothing
            term.write('\x07'); // Bell
          } else if (cleanCompletions.length === 1) {
            // Single completion - apply it
            tabCompletionsRef.current = cleanCompletions;
            tabIndexRef.current = 0;
            applyCompletion(term, cleanCompletions[0]);
          } else {
            // Multiple completions - show them and cycle
            tabCompletionsRef.current = cleanCompletions;
            tabIndexRef.current = 0;
            
            // Show all completions on a new line
            term.write('\r\n');
            term.write('\x1b[1;36m' + cleanCompletions.join('  ') + '\x1b[0m\r\n');
            
            // Rewrite the prompt and current input
            if (message.prompt) {
              term.write(message.prompt);
            }
            term.write(inputBufferRef.current);
          }
        } else if (message.type === 'error') {
          term.writeln(`\r\n\x1b[1;31m✗ Error: ${message.message}\x1b[0m\r\n`);
        } else if (message.type === 'connected') {
          term.writeln(`\x1b[1;36m${message.message}\x1b[0m\r\n`);
        } else if (message.type === 'pong') {
          // Keepalive response - ignore
        }
      } catch (e) {
        // Raw output from terminal
        term.write(event.data);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setConnectionError('Connection error occurred');
    };

    ws.onclose = (event) => {
      const wasConnected = connectedRef.current;
      setConnected(false);
      setConnecting(false);
      connectedRef.current = false;
      
      if (keepaliveIntervalRef.current) {
        clearInterval(keepaliveIntervalRef.current);
        keepaliveIntervalRef.current = null;
      }
      
      if (wasConnected) {
        term.writeln('\r\n\x1b[1;33m⚠ Connection closed\x1b[0m');
        
        // Attempt reconnection if not manually disconnected
        if (reconnectAttempt < MAX_RECONNECT_ATTEMPTS && event.code !== 1000) {
          const nextAttempt = reconnectAttempt + 1;
          setReconnectAttempt(nextAttempt);
          term.writeln(`\x1b[90mReconnecting in 3 seconds... (attempt ${nextAttempt}/${MAX_RECONNECT_ATTEMPTS})\x1b[0m`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connectTerminal(true);
          }, 3000);
        } else if (reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
          term.writeln('\x1b[1;31m✗ Max reconnection attempts reached. Click Connect to try again.\x1b[0m');
          toast.error('Terminal disconnected - max reconnection attempts reached');
        }
      } else {
        if (!connecting) {
          term.writeln('\r\n\x1b[1;31m✗ Failed to connect\x1b[0m');
          toast.error('Terminal connection failed');
        }
      }
    };

    wsRef.current = ws;
  }, [agentId, terminalType, reconnectAttempt, startKeepalive, connecting, applyCompletion]);

  // Disconnect terminal
  const disconnectTerminal = useCallback(() => {
    cleanup();
    setConnected(false);
    setConnecting(false);
    connectedRef.current = false;
    inputBufferRef.current = '';
    setReconnectAttempt(MAX_RECONNECT_ATTEMPTS); // Prevent auto-reconnect
    
    if (xtermRef.current) {
      xtermRef.current.writeln('\r\n\x1b[1;33mDisconnected by user\x1b[0m\r\n');
    }
    toast.info('Terminal disconnected');
  }, [cleanup]);

  // Clear terminal
  const clearTerminal = useCallback(() => {
    if (xtermRef.current) {
      xtermRef.current.clear();
      xtermRef.current.writeln('\x1b[90mTerminal cleared\x1b[0m\r\n');
    }
  }, []);

  // Clear history
  const clearHistory = useCallback(() => {
    commandHistoryRef.current = [];
    historyIndexRef.current = -1;
    toast.success('Command history cleared');
  }, []);

  return (
    <div className="space-y-4">
      <Card className="border-gray-200 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <TerminalIcon className="w-5 h-5 text-blue-600" />
              Remote Terminal
              {connected ? (
                <span className="flex items-center gap-1 text-xs font-normal text-green-600 bg-green-50 px-2 py-1 rounded-full">
                  <Wifi className="w-3 h-3" />
                  Connected
                </span>
              ) : connecting ? (
                <span className="flex items-center gap-1 text-xs font-normal text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Connecting...
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs font-normal text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  <WifiOff className="w-3 h-3" />
                  Disconnected
                </span>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select
                value={terminalType}
                onValueChange={setTerminalType}
                disabled={connected || connecting}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="powershell">PowerShell</SelectItem>
                  <SelectItem value="cmd">Command Prompt</SelectItem>
                </SelectContent>
              </Select>
              
              {!connected && !connecting ? (
                <Button 
                  onClick={() => connectTerminal(false)} 
                  className="gap-2 bg-green-600 hover:bg-green-700"
                >
                  <Power className="w-4 h-4" />
                  Connect
                </Button>
              ) : connecting ? (
                <Button disabled className="gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Connecting...
                </Button>
              ) : (
                <>
                  <Button onClick={clearTerminal} variant="outline" size="sm" className="gap-2">
                    <Trash2 className="w-4 h-4" />
                    Clear
                  </Button>
                  <Button onClick={clearHistory} variant="outline" size="sm" className="gap-1" title="Clear command history">
                    <History className="w-4 h-4" />
                  </Button>
                  <Button onClick={disconnectTerminal} variant="destructive" size="sm" className="gap-2">
                    <Power className="w-4 h-4" />
                    Disconnect
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {connectionError && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4" />
              {connectionError}
            </div>
          )}
          
          <div 
            ref={terminalRef} 
            className="rounded-lg overflow-hidden border border-gray-200"
            style={{ 
              height: '500px',
              backgroundColor: '#1e1e1e',
              padding: '8px'
            }}
          />
          
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <p className="font-medium text-sm text-gray-700 mb-2">Keyboard Shortcuts:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <code className="bg-white px-2 py-0.5 rounded border text-xs">Tab</code>
                <span>Auto-complete</span>
              </div>
              <div className="flex items-center gap-2">
                <code className="bg-white px-2 py-0.5 rounded border text-xs">↑ / ↓</code>
                <span>History</span>
              </div>
              <div className="flex items-center gap-2">
                <code className="bg-white px-2 py-0.5 rounded border text-xs">Ctrl+C</code>
                <span>Cancel</span>
              </div>
              <div className="flex items-center gap-2">
                <code className="bg-white px-2 py-0.5 rounded border text-xs">Ctrl+L</code>
                <span>Clear screen</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Session timeout: 30 minutes of inactivity • Command history: {commandHistoryRef.current?.length || 0} commands stored
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RemoteTerminalTab;
